import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import ApplicabilityWorkspace from './ApplicabilityWorkspace'
import CaseParticipantPanel from './CaseParticipantPanel'
import FactWorkspacePanel from './FactWorkspacePanel'
import EvidenceRailPanel from './EvidenceRailPanel'
import SalaryModulePanel from './SalaryModulePanel'
import CommonLedgerPanel from './CommonLedgerPanel'
import CollaborationPanel from './CollaborationPanel'
import SectionAttestationPanel from './SectionAttestationPanel'
import ExtractionCandidateReviewPanel from './ExtractionCandidateReviewPanel'
import ReconciliationPanel from './ReconciliationPanel'
import CapitalGainsPanel from './CapitalGainsPanel'
import BusinessModulePanel from './BusinessModulePanel'
import InternationalTaxPanel from './InternationalTaxPanel'
import ComputationPanel from './ComputationPanel'
import ReviewReadinessPanel from './ReviewReadinessPanel'
import { getCustomerService } from '@/services/customers'
import { useAuth } from '@/contexts/AuthContext'
import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type {
  ApplicabilityAnswerCode,
  ApplicabilityQuestion,
  PreparationLifecycle,
} from '@/types/returnCase.types'

export default function ReturnCaseWorkspacePage() {
  const { caseId = '' } = useParams()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const workspaceQuery = useQuery({
    queryKey: ['return-case-workspace', caseId],
    queryFn: () => returnCasesHttp.workspace(caseId),
    enabled: Boolean(caseId),
  })
  const customerQuery = useQuery({
    queryKey: ['return-case-customer', workspaceQuery.data?.case.customer_id],
    queryFn: async () =>
      (await getCustomerService().list()).find(
        customer => String(customer.id) === String(workspaceQuery.data?.case.customer_id)
      ),
    enabled: Boolean(workspaceQuery.data),
  })
  const applicabilityQuery = useQuery({
    queryKey: ['return-case-applicability', caseId],
    queryFn: () => returnCasesHttp.applicability(caseId),
    enabled: Boolean(caseId),
  })
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['return-case-workspace', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['return-case-applicability', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['salary-module', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['common-ledger', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['extraction-candidates', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['reconciliations', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['capital-gains', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['business-module', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['international-tax', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['regime-comparison', caseId] }),
      queryClient.invalidateQueries({ queryKey: ['review-readiness', caseId] }),
    ])
  }
  const transition = useMutation({
    mutationFn: (lifecycle_state: PreparationLifecycle) => {
      if (!workspaceQuery.data) throw new Error('Workspace has not loaded')
      return returnCasesHttp.updateShell(caseId, {
        expected_revision: workspaceQuery.data.current_revision,
        lifecycle_state,
        reason: `Move shell to ${lifecycle_state}`,
      })
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Case shell updated')
    },
    onError: error =>
      toast.error(
        'Update rejected',
        error instanceof Error ? error.message : 'Reload the workspace.'
      ),
  })
  const answer = useMutation({
    mutationFn: ({
      question,
      answerCode,
      detail,
    }: {
      question: ApplicabilityQuestion
      answerCode: ApplicabilityAnswerCode
      detail: Record<string, unknown>
    }) => {
      if (!applicabilityQuery.data) throw new Error('Questionnaire has not loaded')
      return returnCasesHttp.saveAnswer(caseId, question.key, {
        expected_revision: applicabilityQuery.data.case_revision,
        answer_code: answerCode,
        detail,
        source: 'questionnaire_web',
      })
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Answer saved and routing refreshed')
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Answer not saved',
        error instanceof Error
          ? error.message
          : 'The case may have changed. Review the refreshed answers.'
      )
    },
  })
  const recompute = useMutation({
    mutationFn: () => {
      if (!applicabilityQuery.data) throw new Error('Questionnaire has not loaded')
      return returnCasesHttp.recomputeRouting(caseId, applicabilityQuery.data.case_revision)
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Routing refreshed')
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Routing not refreshed',
        error instanceof Error ? error.message : 'Review the current answers and rule pack.'
      )
    },
  })
  const override = useMutation({
    mutationFn: ({
      reason,
      citationOrEvidence,
    }: {
      reason: string
      citationOrEvidence: string
    }) => {
      if (!applicabilityQuery.data) throw new Error('Questionnaire has not loaded')
      return returnCasesHttp.overrideRouting(caseId, {
        expected_revision: applicabilityQuery.data.case_revision,
        to_itr: 'ITR-3',
        reason,
        citation_or_evidence: citationOrEvidence,
      })
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Routing override recorded')
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Override not recorded',
        error instanceof Error
          ? error.message
          : 'Only authorized professionals may override eligible recommendations.'
      )
    },
  })

  if (workspaceQuery.isLoading || applicabilityQuery.isLoading)
    return <div className="p-8 text-text-secondary">Loading V2 workspace…</div>
  if (workspaceQuery.isError || !workspaceQuery.data)
    return (
      <div className="p-8">
        <div className="alert alert-error">
          This workspace is unavailable or outside your permitted portfolio.
        </div>
        <Link className="link mt-4 inline-block" to="/return-preparation-cases">
          Return to V2 portfolio
        </Link>
      </div>
    )
  const workspace = workspaceQuery.data
  const customer = customerQuery.data

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col p-4">
      <header className="card mb-4 flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <h1 className="text-xl font-semibold">
            {customer?.fullName ?? `Customer ${workspace.case.customer_id}`} ·{' '}
            {workspace.case.period_value} <span className="badge badge-primary">V2</span>
          </h1>
          <p className="text-sm text-text-secondary">
            Revision {applicabilityQuery.data?.case_revision ?? workspace.current_revision} ·{' '}
            {workspace.case.return_kind} · preparation only
          </p>
        </div>
        <div className="flex gap-2">
          {workspace.allowed_actions
            .filter(actionName => actionName.startsWith('transition:'))
            .map(actionName => {
              const state = actionName.split(':')[1] as PreparationLifecycle
              return (
                <button
                  key={actionName}
                  className="btn btn-secondary"
                  disabled={transition.isPending}
                  onClick={() => transition.mutate(state)}
                >
                  Move to {state}
                </button>
              )
            })}
        </div>
      </header>
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[240px_minmax(0,1fr)_280px]">
        <aside className="card p-4">
          <h2 className="mb-3 font-semibold">Case sections</h2>
          {workspace.sections.map(section => (
            <button
              type="button"
              key={section.key}
              disabled={section.state !== 'available'}
              onClick={() => {
                const target = document.getElementById(`case-section-${section.key}`)
                target?.scrollIntoView({ block: 'start' })
                target?.focus()
              }}
              className={`mb-2 w-full rounded-lg border p-3 text-left focus-visible:ring-2 focus-visible:ring-primary-500 ${section.state === 'available' ? 'border-primary-400 hover:bg-surface-overlay' : 'border-surface-overlay text-text-tertiary'}`}
            >
              <div>{section.title}</div>
              <div className="text-sm text-text-secondary">
                {section.state === 'available' ? 'Available now' : 'Not available for this case'}
              </div>
            </button>
          ))}
        </aside>
        <main className="card min-w-0 p-4 sm:p-6">
          <div id="case-section-applicability" tabIndex={-1} className="scroll-mt-6">
            {applicabilityQuery.isError || !applicabilityQuery.data ? (
              <div className="alert alert-error" role="alert">
                Applicability could not be loaded. Check your access and the approved rule pack,
                then retry.
              </div>
            ) : (
              <ApplicabilityWorkspace
                state={applicabilityQuery.data}
                saving={answer.isPending || recompute.isPending || override.isPending}
                onSave={(question, answerCode, detail) =>
                  answer.mutate({ question, answerCode, detail })
                }
                onRecompute={() => recompute.mutate()}
                onOverride={(reason, citationOrEvidence) =>
                  override.mutate({ reason, citationOrEvidence })
                }
                canOverride={workspace.allowed_actions.includes('routing_override')}
                canEdit={workspace.allowed_actions.includes('applicability_update')}
                canRecompute={workspace.allowed_actions.includes('routing_recompute')}
              />
            )}
          </div>
          <div id="case-section-facts" tabIndex={-1} className="scroll-mt-6">
            <FactWorkspacePanel
              caseId={caseId}
              workspace={workspace}
              canEdit={workspace.allowed_actions.includes('update_shell')}
              onChanged={refresh}
            />
          </div>
          <ExtractionCandidateReviewPanel
            caseId={caseId}
            enabled={workspace.allowed_actions.includes('review_extraction_candidates')}
            onChanged={refresh}
          />
          <ReconciliationPanel
            caseId={caseId}
            enabled={workspace.allowed_actions.includes('prepare_reconciliation')}
            canResolve={workspace.allowed_actions.includes('resolve_reconciliation')}
            canImport={workspace.allowed_actions.includes('start_official_import')}
            modules={workspace.modules ?? []}
            onChanged={refresh}
          />
          {workspace.sections.some(
            section => section.key === 'salary' && section.state === 'available'
          ) && (
            <div id="case-section-salary" tabIndex={-1} className="scroll-mt-6">
              <SalaryModulePanel
                caseId={caseId}
                customerName={customer?.fullName}
                canEdit={workspace.allowed_actions.includes('update_shell')}
                onChanged={refresh}
              />
            </div>
          )}
          <div id="case-section-income" tabIndex={-1} className="scroll-mt-6">
            <CommonLedgerPanel
              caseId={caseId}
              canEdit={workspace.allowed_actions.includes('update_shell')}
              onChanged={refresh}
            />
          </div>
          <div id="case-section-capital-gains" tabIndex={-1} className="scroll-mt-6">
            <CapitalGainsPanel
              caseId={caseId}
              canImport={workspace.allowed_actions.includes('update_shell')}
              canFlag={workspace.allowed_actions.includes('update_shell')}
              onChanged={refresh}
            />
          </div>
          <div id="case-section-business" tabIndex={-1} className="scroll-mt-6">
            <BusinessModulePanel
              caseId={caseId}
              canEdit={workspace.allowed_actions.includes('update_shell')}
              onChanged={refresh}
            />
          </div>
          <div id="case-section-international" tabIndex={-1} className="scroll-mt-6">
            <InternationalTaxPanel
              caseId={caseId}
              canEdit={workspace.allowed_actions.includes('update_shell')}
              onChanged={refresh}
            />
          </div>
          <div id="case-section-computation" tabIndex={-1} className="scroll-mt-6">
            <ComputationPanel
              caseId={caseId}
              currentRevision={workspace.current_revision}
              canRun={workspace.allowed_actions.includes('run_computation')}
            />
          </div>
          <CollaborationPanel
            caseId={caseId}
            client={returnCasesHttp}
            mode="staff"
            defaultOwnerId={workspace.case.assigned_to_user_id ?? workspace.case.created_by}
            onChanged={refresh}
          />
          <SectionAttestationPanel
            caseId={caseId}
            sectionKeys={[...new Set((workspace.modules ?? []).map(module => module.section_key))]}
            client={returnCasesHttp}
            mode="staff"
            canAct={workspace.allowed_actions.includes('verify_section')}
            onChanged={refresh}
          />
          <div id="case-section-review" tabIndex={-1} className="scroll-mt-6">
            <ReviewReadinessPanel caseId={caseId} actorUserId={user?.id} onChanged={refresh} />
          </div>
        </main>
        <aside className="card p-4">
          <h2 className="font-semibold">Context rail</h2>
          {customer && (
            <dl className="mt-3 space-y-3 text-sm" aria-label="Customer profile">
              {[
                ['PAN', customer.pan],
                ['Aadhaar', customer.aadhaar ?? 'Not provided'],
                ['Email', customer.emailPrimary],
                ['Mobile', customer.mobilePrimary],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="text-text-secondary">{label}</dt>
                  <dd className="break-words">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          <dl className="mt-3 space-y-3 text-sm">
            <div>
              <dt className="text-text-secondary">Customer</dt>
              <dd>
                <Link className="link" to={workspace.links.customer}>
                  {customer?.fullName ?? workspace.case.customer_id}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">Period basis</dt>
              <dd>{workspace.case.period_basis}</dd>
            </div>
            <div>
              <dt className="text-text-secondary">Assignment</dt>
              <dd>{workspace.case.assigned_to_user_id ?? 'Unassigned'}</dd>
            </div>
            <div>
              <dt className="text-text-secondary">Rule pack</dt>
              <dd>
                {applicabilityQuery.data?.questionnaire.rule_pack.semantic_version ?? 'Unavailable'}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">Routing hash</dt>
              <dd className="break-all font-mono text-xs">
                {applicabilityQuery.data?.routing?.inputs_hash ?? 'Not evaluated'}
              </dd>
            </div>
          </dl>
          <div className="mt-6 border-t border-border pt-5">
            <div id="case-section-documents" tabIndex={-1} className="scroll-mt-6">
              <EvidenceRailPanel
                caseId={caseId}
                customerId={workspace.case.customer_id}
                assessmentYear={workspace.case.period_value}
                currentRevision={workspace.current_revision}
                checklist={applicabilityQuery.data?.routing?.checklist ?? []}
                facts={workspace.facts ?? []}
                canUpload={workspace.allowed_actions.includes('upload_evidence')}
                canLink={workspace.allowed_actions.includes('link_fact_evidence')}
                onChanged={refresh}
              />
            </div>
          </div>
        </aside>
      </div>
      {workspace.allowed_actions.includes('manage_participants') && (
        <CaseParticipantPanel caseId={caseId} customer={customer} />
      )}
    </div>
  )
}
